import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaArrowUp, FaArrowDown, FaCloudUploadAlt, FaDatabase, FaHistory, FaServer, FaUser, FaChartLine } from 'react-icons/fa';
import { useMaintenance } from '../../contexts/MaintenanceContext';
Chart.register(ArcElement, Tooltip, Legend);

const mockAnalytics = {
  activeUsers: { value: 14, change: 55.6 },
  retentionDay1: { value: 0, change: -100 },
  buildSize: { value: 151, change: 262.7 }, // MB
  downloads: { value: 151, change: 262.7 }, // MB
  hosting: { value: 151, change: 262.7 }, // MB
  firestoreReads: { value: 358, change: 311.5 },
  firestoreWrites: { value: 16, change: -55.6 },
  storage: { value: 97.4, change: 1154.1 }, // MB
  lastDeploy: {
    date: '10 jul 2025 1:16 a.m.',
    by: 'flexmda.adm@gmail.com',
    service: 'Hosting',
  },
};

const SiteSettings: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'status' | 'config'>('status');
  const { maintenance, setMaintenance } = useMaintenance();

  const Card = ({ icon, title, value, unit, change, positive }: { icon: React.ReactNode, title: string, value: string | number, unit?: string, change: number, positive: boolean }) => (
    <div className="bg-white rounded-2xl shadow p-6 flex flex-col items-start min-w-[220px] max-w-[260px] border border-white/40">
      <div className="flex items-center gap-3 mb-2 text-gray-700">
        <span className="text-xl text-red-600">{icon}</span>
        <span className="font-semibold text-base">{title}</span>
      </div>
      <div className="flex items-end gap-2 mb-1">
        <span className="text-3xl font-bold text-gray-900">{value}</span>
        {unit && <span className="text-base text-gray-500 font-medium">{unit}</span>}
      </div>
      <div className={`flex items-center gap-1 text-sm font-semibold ${positive ? 'text-green-600' : 'text-red-600'}`}> 
        {positive ? <FaArrowUp /> : <FaArrowDown />} {Math.abs(change)}%
      </div>
    </div>
  );

  return (
    <div className="p-8 w-full min-h-screen bg-gray-100 font-['Montserrat'] relative overflow-hidden">
      <div className="min-h-screen w-full bg-gradient-to-br from-red-50 via-white to-red-100 py-12 px-4 flex flex-col items-center justify-start">
        <div className="max-w-5xl w-full mx-auto">
          {/* Header visual coherente */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-10 mb-10">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-2 tracking-tight drop-shadow-lg">
              <span className="bg-gradient-to-r from-red-500 via-pink-400 to-red-600 bg-clip-text text-transparent">
                {t('admin.siteSettings.title')}
              </span>
            </h1>
            <p className="text-gray-700 text-lg mt-4">{t('admin.siteSettings.description')}</p>
          </div>
          {/* Tabs */}
          <div className="flex gap-4 mb-8 border-b-2 border-gray-200 pb-4">
            <button
              onClick={() => setActiveTab('status')}
              className={`py-3 px-6 border-none rounded-lg cursor-pointer text-base font-medium transition-all duration-200 ease-in-out ${
                activeTab === 'status'
                  ? 'bg-red-600 text-white font-semibold'
                  : 'bg-transparent text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t('admin.siteSettings.statusTab')}
            </button>
            <button
              onClick={() => setActiveTab('config')}
              className={`py-3 px-6 border-none rounded-lg cursor-pointer text-base font-medium transition-all duration-200 ease-in-out ${
                activeTab === 'config'
                  ? 'bg-red-600 text-white font-semibold'
                  : 'bg-transparent text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t('admin.siteSettings.configTab')}
            </button>
          </div>
          {/* Contenido de los tabs */}
          {activeTab === 'status' && (
            <div className="flex flex-col gap-8">
              {/* Switch de mantenimiento */}
              <div className="flex items-center gap-4 mb-6">
                <label className="font-semibold text-lg text-gray-800 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={maintenance}
                    onChange={e => setMaintenance(e.target.checked)}
                    className="w-5 h-5 accent-red-600 rounded focus:ring-2 focus:ring-red-500"
                  />
                  Modo mantenimiento
                </label>
                <span className={`text-sm font-semibold ${maintenance ? 'text-red-600' : 'text-green-600'}`}>{maintenance ? 'ACTIVADO' : 'DESACTIVADO'}</span>
              </div>
              {/* Tarjetas de métricas principales */}
              <div className="flex flex-wrap gap-6 justify-center mb-8">
                <Card icon={<FaUser />} title="Usuarios activos por día" value={mockAnalytics.activeUsers.value} change={mockAnalytics.activeUsers.change} positive={mockAnalytics.activeUsers.change > 0} />
                <Card icon={<FaChartLine />} title="Retención del día 1" value={mockAnalytics.retentionDay1.value + '%'} change={mockAnalytics.retentionDay1.change} positive={mockAnalytics.retentionDay1.change > 0} />
                <Card icon={<FaCloudUploadAlt />} title="Compilación / Hosting / Downloads (7 días)" value={mockAnalytics.buildSize.value} unit="MB" change={mockAnalytics.buildSize.change} positive={mockAnalytics.buildSize.change > 0} />
                <Card icon={<FaDatabase />} title="Firestore Lecturas" value={mockAnalytics.firestoreReads.value} change={mockAnalytics.firestoreReads.change} positive={mockAnalytics.firestoreReads.change > 0} />
                <Card icon={<FaDatabase />} title="Firestore Escrituras" value={mockAnalytics.firestoreWrites.value} change={mockAnalytics.firestoreWrites.change} positive={mockAnalytics.firestoreWrites.change > 0} />
                <Card icon={<FaServer />} title="Storage (actual)" value={mockAnalytics.storage.value} unit="MB" change={mockAnalytics.storage.change} positive={mockAnalytics.storage.change > 0} />
              </div>
              {/* Historial de implementaciones */}
              <div className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40 flex flex-col items-start mb-8">
                <div className="flex items-center gap-3 mb-4 text-gray-700 text-lg font-bold">
                  <FaHistory className="text-xl text-red-600" />
                  Historial de implementaciones
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex flex-wrap gap-8 items-center w-full">
                    <div className="flex flex-col gap-1">
                      <span className="text-gray-800 font-semibold">Implementado</span>
                      <span className="text-gray-600 text-sm">{mockAnalytics.lastDeploy.date}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-gray-800 font-semibold">Implementado por</span>
                      <span className="text-gray-600 text-sm">{mockAnalytics.lastDeploy.by}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-gray-800 font-semibold">Servicio</span>
                      <span className="text-gray-600 text-sm">{mockAnalytics.lastDeploy.service}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'config' && (
            <div className="bg-white rounded-3xl p-10 shadow-2xl border border-white/40 flex flex-col items-center justify-center min-h-[300px]">
              <span className="text-gray-500 text-lg">{t('admin.siteSettings.comingSoon')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteSettings; 